import React, { useContext, useState, useEffect, Fragment } from "react";
import history from "../services/history";
import {
  Typography,
  Box,
  Divider,
  Button,
  Grid,
  OutlinedInput,
  FormControl,
  FormHelperText,
  IconButton,
  Popover,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Trans, useTranslation } from "react-i18next";
import API from "../services/api";
import { StartpopSettingsContext } from "../contexts/SettingsContext";
import { useMount } from "react-use";
import {
  ArrowForwardIosOutlined,
  ChevronLeft,
  ChevronRight,
  Delete,
  HelpOutline,
} from "@mui/icons-material";
import StartpopOrderSideBar from "./StartpopOrderSideBar";
import WBPPage from "./sub-components/WBPPage";
import UserContext from "../contexts/UserContext";
import UIContext from "../contexts/UIContext";
import DonationSummary from "./sub-components/DonationSummary";
import Client from "shopify-buy";
import references from "../services/references";
import CornutaWarningModal from "./modals/CornutaWarningModal";
import StartpopOrderContext from "../contexts/StartpopOrderContext";
import {
  DonationSelector,
  SummaryAddress,
} from "./sub-components/SummaryComponents";
import moment from "moment";
import InfoIcon from "@mui/icons-material/Info";
import AdditionalProducts from "./sub-components/AdditionalProducts";
import ProductLine from "./sub-components/ProductLine";

let checkoutURL = "";

const useStyles = makeStyles((theme) => ({
  formRoot: { width: "100%" },
  btnSize: {
    [theme.breakpoints.up("sm")]: {
      marginBottom: "10px",
      width: "100%",
    },
  },
  padless: {
    padding: 7,
  },
  countColumn: {
    textAlign: "center",
  },
  itemColumn: {},
  priceColumn: {
    textAlign: "end",
  },
  deleteColumn: {
    textAlign: "end",
  },
  mobiWidth: {
    [theme.breakpoints.down("md")]: {
      marginBottom: theme.spacing(1),
      width: "100%",
    },
  },
  totalPrice: {
    color: theme.palette.primary.main,
    fontSize: theme.typography.h4.fontSize,
    fontWeight: "bold",
    [theme.breakpoints.down("lg")]: {
      fontSize: theme.typography.h5.fontSize,
    },
    [theme.breakpoints.down("md")]: {
      fontSize: theme.typography.h6.fontSize,
    },
  },
  divider: {
    width: "100%",
  },
  fullWidth: {
    width: "100%",
  },
  deleteIconButton: {
    padding: 0,
  },
}));

const StartpopOrderSummary = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const classes = useStyles();
  const [voucherCode, setVoucherCode] = useState("");
  const [user] = useContext(UserContext);
  const [, , loader] = useContext(UIContext);
  const [voucherError, setVoucherError] = useState("");
  const [currency, setCurrency] = useState("");
  const [price, setPrice] = useState(references.defaultOrders.beecare.price);
  const [anchorEl, setAnchorEl] = useState(null);
  const openPopover = Boolean(anchorEl);
  const popoverId = open ? "simple-popover" : undefined;

  const settings = useContext(StartpopSettingsContext);
  const [acceptedWarning, setAcceptedWarning] = useState(false);
  const [warningModalOpenStatus, setWarningModalOpenStatus] = useState(false);
  const [startpopOrder, setStartpopOrder] = useContext(StartpopOrderContext);
  const warningTexts = references.defaultOrders.startpop.warningTexts;
  const titleTranslations = startpopOrder.titleTranslations;

  // Initializing a client to return content in the store's primary language
  // const shopifyClient = Client.buildClient({
  //   domain: new URL(
  //     JSON.parse(process.env.REACT_APP_SHOPIFY_URL)[i18n.language]
  //   ).host,
  //   storefrontAccessToken: JSON.parse(
  //     process.env.REACT_APP_SHOPIFY_ACCESSTOKEN
  //   )[i18n.language],
  //   language: i18n.language,
  // });

  const [additionalProducts, setAdditionalProducts] = useState(
    startpopOrder.additionalProducts || []
  );

  useMount(() => {
    if (!startpopOrder.amount) {
      history.push("/startpops");
    }
  });

  useEffect(() => {
    setCurrency(settings ? settings.currency : "");
  }, [settings.currency]);

  useEffect(() => {
    if (startpopOrder.price) {
      setPrice(Number(startpopOrder.price));
    }
  }, [startpopOrder.variant]);

  const sendData = async () => {
    try {
      loader.addTask("ORDER");
      loader.addTask("CHECKOUT");
      let shopifyUrl = JSON.parse(process.env.REACT_APP_SHOPIFY_URL)[
        i18n.language
      ];
      console.log("Shopify URL:", shopifyUrl);
      // Step 1: Create cart
      const cartCreateResponse = await fetch(
        `${shopifyUrl}/api/2023-10/graphql.json`, // Update with your store URL
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": JSON.parse(
              process.env.REACT_APP_SHOPIFY_ACCESSTOKEN
            )[i18n.language], // Replace with your access token
          },
          body: JSON.stringify({
            query: `
            mutation cartCreate {
              cartCreate {
                cart {
                  id
                  checkoutUrl
                }
                userErrors {
                  message
                  field
                }
              }
            }
          `,
          }),
        }
      );

      const cartData = await cartCreateResponse.json();

      if (
        cartData.errors ||
        (cartData.data.cartCreate.userErrors &&
          cartData.data.cartCreate.userErrors.length > 0)
      ) {
        console.error(
          "Cart creation failed:",
          cartData.errors || cartData.data.cartCreate.userErrors
        );
        shopifyError(cartData.errors || cartData.data.cartCreate.userErrors);
        return;
      }

      const cartId = cartData.data.cartCreate.cart.id;

      // Step 2: Add cart attributes
      const attributesResponse = await fetch(
        `${shopifyUrl}/api/2023-10/graphql.json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": JSON.parse(
              process.env.REACT_APP_SHOPIFY_ACCESSTOKEN
            )[i18n.language],
          },
          body: JSON.stringify({
            query: `
            mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
              cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
                cart {
                  id
                }
                userErrors {
                  message
                  field
                }
              }
            }
          `,
            variables: {
              cartId: cartId,
              attributes: [
                {
                  key: "deliveryDate",
                  value: moment
                    .parseZone(startpopOrder.delivery_on)
                    .format("DD-MM-YYYY"),
                },
                {
                  key: "seasonType",
                  value: "startpops",
                },
              ],
            },
          }),
        }
      );

      const attributesData = await attributesResponse.json();

      if (
        attributesData.errors ||
        (attributesData.data.cartAttributesUpdate.userErrors &&
          attributesData.data.cartAttributesUpdate.userErrors.length > 0)
      ) {
        console.error(
          "Cart attributes update failed:",
          attributesData.errors ||
            attributesData.data.cartAttributesUpdate.userErrors
        );
        shopifyError(
          attributesData.errors ||
            attributesData.data.cartAttributesUpdate.userErrors
        );
        return;
      }

      // Step 3: Set buyer identity with shipping address
      const selectedAddress = user.addresses.find(
        (address) => address.id === startpopOrder.address_id
      );

      const buyerIdentityResponse = await fetch(
        `${shopifyUrl}/api/2023-10/graphql.json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": JSON.parse(
              process.env.REACT_APP_SHOPIFY_ACCESSTOKEN
            )[i18n.language],
          },
          body: JSON.stringify({
            query: `
            mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
              cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
                cart {
                  id
                }
                userErrors {
                  message
                  field
                }
              }
            }
          `,
            variables: {
              cartId: cartId,
              buyerIdentity: {
                email: user.email, // Assuming user object has email
                deliveryAddressPreferences: [
                  {
                    deliveryAddress: {
                      address1: selectedAddress.address_1,
                      address2: selectedAddress.address_2,
                      city: selectedAddress.town,
                      company: selectedAddress.company_name,
                      country: selectedAddress.country_code,
                      firstName: selectedAddress.first_name,
                      lastName: selectedAddress.last_name,
                      phone: "",
                      province: "",
                      zip: selectedAddress.postal_code,
                    },
                  },
                ],
              },
            },
          }),
        }
      );

      const buyerIdentityData = await buyerIdentityResponse.json();

      if (
        buyerIdentityData.errors ||
        (buyerIdentityData.data.cartBuyerIdentityUpdate.userErrors &&
          buyerIdentityData.data.cartBuyerIdentityUpdate.userErrors.length > 0)
      ) {
        console.error(
          "Buyer identity update failed:",
          buyerIdentityData.errors ||
            buyerIdentityData.data.cartBuyerIdentityUpdate.userErrors
        );
        shopifyError(
          buyerIdentityData.errors ||
            buyerIdentityData.data.cartBuyerIdentityUpdate.userErrors
        );
        return;
      }

      // Step 4: Add line items
      const lineItemsToAdd = [];
      const quantity =
        Number(startpopOrder.amount) - startpopOrder.discount_vouchers.length;

      lineItemsToAdd.push({
        merchandiseId: startpopOrder.variant.id,
        quantity: quantity,
      });

      if (startpopOrder.discount_vouchers.length > 0) {
        const freeVariant = settings.shopify.products.variants.find(
          (variant) => {
            let freePrice = variant.price;
            if (typeof freePrice === "object" && freePrice.amount) {
              freePrice = freePrice.amount;
            }
            return Number(freePrice) === 0 ? true : false;
          }
        );

        lineItemsToAdd.push({
          merchandiseId: freeVariant.id,
          quantity: startpopOrder.discount_vouchers.length,
          attributes: [
            {
              key: "vouchers",
              value: startpopOrder.discount_vouchers
                .map((voucher) => voucher.code)
                .join(", "),
            },
          ],
        });
      }

      if (startpopOrder.donation > 0) {
        const donationVariant = settings.shopify.donationOptions.variants.find(
          (variant) => variant.sku === "donation-" + startpopOrder.donation
        );

        lineItemsToAdd.push({
          merchandiseId: donationVariant.id,
          quantity: 1,
        });
      }

      if (startpopOrder.additionalProducts) {
        startpopOrder.additionalProducts.forEach((product) => {
          lineItemsToAdd.push({
            merchandiseId: product.id,
            quantity: product.quantity,
          });
        });
      }

      // Filter out items with quantity 0
      const filteredLineItems = lineItemsToAdd.filter(
        (item) => item.quantity > 0
      );

      // Add line items one by one to handle custom attributes properly
      for (const item of filteredLineItems) {
        const lineItemResponse = await fetch(
          `${shopifyUrl}/api/2023-10/graphql.json`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token": JSON.parse(
                process.env.REACT_APP_SHOPIFY_ACCESSTOKEN
              )[i18n.language],
            },
            body: JSON.stringify({
              query: `
              mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
                cartLinesAdd(cartId: $cartId, lines: $lines) {
                  cart {
                    id
                    checkoutUrl
                  }
                  userErrors {
                    message
                    field
                  }
                }
              }
            `,
              variables: {
                cartId: cartId,
                lines: [
                  {
                    merchandiseId: item.merchandiseId,
                    quantity: item.quantity,
                    attributes: item.attributes || [],
                  },
                ],
              },
            }),
          }
        );

        const lineItemData = await lineItemResponse.json();

        if (
          lineItemData.errors ||
          (lineItemData.data.cartLinesAdd.userErrors &&
            lineItemData.data.cartLinesAdd.userErrors.length > 0)
        ) {
          console.error(
            "Line item addition failed:",
            lineItemData.errors || lineItemData.data.cartLinesAdd.userErrors
          );
          shopifyError(
            lineItemData.errors || lineItemData.data.cartLinesAdd.userErrors
          );
          return;
        }

        // Update checkout URL after each line item addition
        // checkoutURL = lineItemData.data.cartLinesAdd.cart.checkoutUrl;
      }

      // Get final cart data to obtain the checkout URL
      const cartQueryResponse = await fetch(
        `${shopifyUrl}/api/2023-10/graphql.json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": JSON.parse(
              process.env.REACT_APP_SHOPIFY_ACCESSTOKEN
            )[i18n.language],
          },
          body: JSON.stringify({
            query: `
            query getCart($cartId: ID!) {
              cart(id: $cartId) {
                checkoutUrl
              }
            }
          `,
            variables: {
              cartId: cartId,
            },
          }),
        }
      );

      const cartQueryData = await cartQueryResponse.json();

      if (cartQueryData.errors) {
        console.error("Cart query failed:", cartQueryData.errors);
        shopifyError(cartQueryData.errors);
        return;
      }

      const checkoutURL = cartQueryData.data.cart.checkoutUrl;
      let finalCheckoutURL = checkoutURL;

      if (i18n.language === "fr-CH") {
        finalCheckoutURL += "&locale=fr";
      }

      gotoShopifyCheckout(finalCheckoutURL);
    } catch (error) {
      console.error("Error in cart process:", error);
      shopifyError(error);
    }
  };

  const gotoShopifyCheckout = (checkoutURL) => {
    API.get(`me/shopify-login?return_to=` + checkoutURL)
      .then((response) => {
        loader.clearTask("CHECKOUT");
        window.open(response.data.url, "_self");
      })
      .catch(shopifyError);
  };

  const shopifyError = (error) => {
    loader.clearTask("CHECKOUT");
    console.warn(error);
  };

  const calculateTotals = (
    donation = 0,
    discount_vouchers = 0,
    productsList = additionalProducts
  ) => {
    let startpopPrice = startpopOrder.amount ? startpopOrder.amount * price : 0;
    let discounts = discount_vouchers ? discount_vouchers.length * price : 0;
    let additionalProductsPrice = productsList.reduce(
      (sum, product) => sum + product.price * product.quantity,
      0
    );

    console.log("Updated additionalProductsPrice:", additionalProductsPrice);

    const totalPrice =
      startpopPrice + additionalProductsPrice - discounts + donation;

    setStartpopOrder((oldOrder) => ({
      ...oldOrder,
      total_price: totalPrice,
    }));

    return totalPrice; // Return the new total, don't call setState here
  };

  const deleteAllDiscounts = (event) => {
    event.preventDefault();
    setStartpopOrder((oldOrder) => ({
      ...oldOrder,
      discount_vouchers: oldOrder.validated_auto_vouchers,
    }));
    calculateTotals(
      startpopOrder.donation,
      startpopOrder.validated_auto_vouchers
    );
  };
  const addProductToOrder = (variant) => {
    console.log(variant);

    setStartpopOrder((oldOrder) => {
      const updatedProducts = oldOrder.additionalProducts
        ? [...oldOrder.additionalProducts]
        : []; // Ensure it's an array

      const existingProduct = updatedProducts.find((p) => p.id === variant.id);

      if (existingProduct) {
        existingProduct.quantity += 1;
      } else {
        updatedProducts.push({
          ...variant,
          quantity: 1,
          price: variant.priceV2.amount,
        });
        setAdditionalProducts(updatedProducts);
      }

      const newTotal =
        (Number(oldOrder.total_price) || 0) + Number(variant.priceV2.amount); // Ensure total price is not undefined
      console.log(oldOrder);
      return {
        ...oldOrder,
        additionalProducts: updatedProducts,
        total_price: newTotal,
      };
    });
  };

  // toggleDonations is what we use to differentiate toggleable donation buttons from the custom donation
  const submitDonation = (
    event,
    donationValue,
    shouldResetDonation = false
  ) => {
    event.preventDefault();

    const updatedDonation =
      shouldResetDonation && startpopOrder.donation === donationValue
        ? 0
        : donationValue;

    setStartpopOrder((oldOrder) => ({
      ...oldOrder,
      donation: updatedDonation,
    }));

    calculateTotals(updatedDonation, startpopOrder.discount_vouchers);
  };

  const addVoucherHandler = (event) => {
    event.preventDefault();
    setVoucherError("");
    setVoucherCode(event.target.value.toUpperCase().trim());
  };

  const submitVoucherCode = (event) => {
    event.preventDefault();
    setVoucherError("");
    if (voucherCode.toUpperCase().indexOf("FREECARE") < 0) {
      if (
        !startpopOrder.discount_vouchers.some(
          (voucher) => voucher.code === voucherCode
        )
      ) {
        loader.addTask("VOUCHER");
        const voucherType =
          settings.osmiaTypeSeason === "cornuta"
            ? "STARTPOP_OC"
            : settings.osmiaTypeSeason === "bicornis"
            ? "STARTPOP_BC"
            : "STARTPOP";
        API.post(`me/vouchers`, { code: voucherCode, type: voucherType })
          .then((res) => {
            loader.clearTask("VOUCHER");
            const discount_vouchers = [...startpopOrder.discount_vouchers];
            discount_vouchers.push(res.data);
            setVoucherCode("");
            setStartpopOrder((oldOrder) => ({
              ...oldOrder,
              discount_vouchers: discount_vouchers,
            }));
            calculateTotals(startpopOrder.donation, discount_vouchers);
          })
          .catch((error) => {
            loader.clearTask("VOUCHER");
            console.warn(error);
            let errorText = error.response.data.detail;
            switch (error.response.data.status) {
              case 403:
                errorText = "495";
                break;
              case 400:
                if (errorText === "Invalid code") {
                  errorText = "501";
                } else if (errorText === "Invalid Beecare code") {
                  errorText = "502";
                } else if (errorText === "Voucher is for STARTPOP_OC") {
                  errorText = "504";
                } else if (errorText === "Voucher is for STARTPOP_BC") {
                  errorText = "503";
                }
                break;
            }
            setVoucherError(t(errorText));
          });
      } else {
        loader.clearTask("VOUCHER");
        setVoucherError(t("Voucher code has already been entered"));
      }
    } else {
      loader.clearTask("VOUCHER");
      setVoucherError(t("Invalid code"));
    }
  };
  function areSameDay(date1, date2) {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  }

  const submitHandler = (event) => {
    event.preventDefault();
    const today = new Date();
    if (
      startpopOrder.deliveryChosenOn &&
      !areSameDay(startpopOrder.deliveryChosenOn, today)
    ) {
      window.location.href = "/startpop";
      return;
    }
    if (acceptedWarning === false && settings.osmiaTypeSeason === "cornuta") {
      setWarningModalOpenStatus(true);
      return;
    }

    if (startpopOrder.order_date !== moment.utc().format("YYYY-MM-DD")) {
      location.reload();
    } else {
      sendData(Number(startpopOrder.total_price) === 0 ? true : false);
    }
  };
  const handleDelete = (product) => {
    console.log("Deleting:", product);

    // Filter out the deleted product
    const updatedProducts = additionalProducts.filter(
      (item) => item.id !== product
    );

    // Update state (this triggers a re-render)
    setAdditionalProducts(updatedProducts);
    const newTotal = calculateTotals(
      startpopOrder.donation ?? 0,
      startpopOrder.discount_vouchers ?? 0,
      updatedProducts // Pass updated products directly
    );
    setStartpopOrder((oldOrder) => {
      return {
        ...oldOrder,
        additionalProducts: updatedProducts,
        total_price: newTotal,
      };
    });
  };

  return (
    <div>
      <StartpopOrderSideBar step={3} showSummary>
        <WBPPage
          title={t("490")}
          description={t("496", {
            voucherText:
              startpopOrder.discount_vouchers.length !==
                Number(startpopOrder.amount) &&
              settings.osmiaTypeSeason === "bicornis"
                ? t("485")
                : "",
          })}
        >
          <Box mt={-1} mb={2}>
            <Grid container alignItems="center">
              <SummaryAddress
                shippingAddressID={startpopOrder.address_id}
                billingHeader="486"
                alternativeHeader="486"
                showBillingAddress={false}
              />
              <Grid container direction="row" pt={1} alignItems="flex-start">
                <Grid item xs={7}>
                  <Typography variant="h6">{t("Order summary")}</Typography>
                </Grid>
                <Grid
                  item
                  xs={2} // Adjusted to 5 to ensure proper spacing
                  container
                  direction="row"
                  justifyContent="flex-end" // Aligns content to the right
                >
                  <Typography variant="caption" textAlign="right">
                    {t("deliveryInfo")}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container direction="row" pt={1} alignItems="flex-start">
                <Grid item xs={1} className={classes.countColumn}>
                  <Typography variant="body1">
                    {startpopOrder.amount}
                  </Typography>
                </Grid>
                <Grid item xs={6} className={classes.itemColumn}>
                  <Typography variant="body1">
                    <Trans i18nKey={titleTranslations.mainTitle} />
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {"(" +
                      t(
                        settings.osmiaTypeSeason === "bicornis"
                          ? "Osmia bicornis"
                          : "Osmia cornuta"
                      ) +
                      ")"}
                  </Typography>
                </Grid>
                {/* Delivery Time Column */}
                <Grid item xs={2} className={classes.deliveryColumn}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    textAlign="right"
                  >
                    {moment
                      .parseZone(startpopOrder.delivery_on)
                      .format("DD-MM-YYYY")}
                  </Typography>
                </Grid>
                <Grid item xs={2} className={classes.priceColumn}>
                  <Typography variant="body1">
                    {currency +
                      " " +
                      parseFloat(startpopOrder.amount * price).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={1} className={classes.deleteColumn}>
                  &nbsp;
                </Grid>
              </Grid>

              <Grid container direction="row" py={1}>
                <Grid item xs={1} className={classes.countColumn}>
                  &nbsp;
                </Grid>
                <Grid
                  item
                  xs
                  className={classes.itemTextColumn}
                  alignSelf="center"
                ></Grid>
              </Grid>
              {startpopOrder.discount_vouchers.length > 0 && (
                <Grid
                  container
                  item
                  direction="row"
                  py={1}
                  alignItems="flex-start"
                >
                  <Grid item xs={1} className={classes.countColumn}>
                    <Typography variant="body1">
                      {startpopOrder.discount_vouchers.length}
                    </Typography>
                  </Grid>
                  <Grid item xs={7} className={classes.itemColumn}>
                    <Typography variant="body1">
                      {titleTranslations.voucherTitle}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t("489")}
                    </Typography>
                    {startpopOrder.discount_vouchers.map((voucher, key) => {
                      return (
                        <Typography
                          key={key}
                          variant="body2"
                          color="textSecondary"
                        >
                          {voucher.code}
                        </Typography>
                      );
                    })}
                  </Grid>
                  <Grid item xs={3} className={classes.priceColumn}>
                    <Typography variant="body1">
                      {"- " +
                        currency +
                        " " +
                        parseFloat(
                          startpopOrder.discount_vouchers.length * price
                        ).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={1} className={classes.deleteColumn}>
                    {startpopOrder.discount_vouchers.filter(
                      (voucher) => voucher.client_id === null
                    ).length > 0 && (
                      <IconButton
                        className={classes.deleteIconButton}
                        onClick={deleteAllDiscounts}
                        size="small"
                      >
                        <Delete></Delete>
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              )}
              {startpopOrder.donation > 0 && (
                <Grid container direction="row" py={1} alignItems="center">
                  <Grid item xs={1} className={classes.countColumn}>
                    <Typography variant="body1">1</Typography>
                  </Grid>
                  <Grid item xs={7} className={classes.itemColumn}>
                    <Typography variant="body1">
                      {titleTranslations.donationTitle}
                    </Typography>
                  </Grid>
                  <Grid item xs={3} className={classes.priceColumn}>
                    <Typography variant="body1">
                      {currency +
                        " " +
                        Number(startpopOrder.donation).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={1} className={classes.deleteColumn}>
                    <IconButton
                      className={classes.deleteIconButton}
                      onClick={submitDonation}
                      size="small"
                    >
                      <Delete></Delete>
                    </IconButton>
                  </Grid>
                </Grid>
              )}
              {additionalProducts &&
                additionalProducts.length > 0 &&
                additionalProducts.map((product, index) => (
                  <ProductLine
                    key={index}
                    product={product}
                    currency={currency}
                    onDelete={handleDelete}
                  />
                ))}
              <Divider className={classes.divider} />
              <Grid container direction="row" py={1}>
                <Grid item xs={1} className={classes.countColumn}>
                  &nbsp;
                </Grid>
                <Grid
                  item
                  xs={7}
                  className={classes.itemTextColumn}
                  alignSelf="center"
                >
                  <Typography variant="body1">{t("Total incl VAT")}</Typography>
                </Grid>
                <Grid
                  item
                  xs={4}
                  className={classes.priceColumn}
                  alignSelf="center"
                >
                  <Typography className={classes.totalPrice}>
                    {currency +
                      " " +
                      Number(startpopOrder.total_price).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
              <Divider className={classes.divider} />
              <form
                onSubmit={submitVoucherCode}
                className={classes.formRoot}
                hidden={
                  Number(startpopOrder.amount) <=
                    startpopOrder.discount_vouchers.length ||
                  settings.osmiaTypeSeason === "cornuta"
                }
              >
                <Grid
                  container
                  direction="row"
                  item
                  xs={12}
                  pt={1}
                  spacing={{ xs: 1 }}
                  justifyContent={{ xs: "flex-start", sm: "center" }}
                  alignItems={"flex-start"}
                  style={{ margin: 0 }}
                >
                  <Grid
                    item
                    xs={12}
                    sm={4}
                    className={classes.itemColumn}
                    alignSelf={{ xs: "center", sm: "flex-start" }}
                  >
                    <Typography variant="body1">
                      {t("488")}
                      <Tooltip
                        title={
                          <h3>
                            <Trans i18nKey="500" />
                          </h3>
                        }
                        arrow
                      >
                        <IconButton
                          edge="end"
                          aria-label="info"
                          color="primary"
                          size="small"
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} className={classes.priceColumn}>
                    <FormControl fullWidth>
                      <OutlinedInput
                        id="ticket_code"
                        variant="outlined"
                        name="ticket_code"
                        onChange={addVoucherHandler}
                        placeholder={t("489")}
                        value={voucherCode}
                        fullWidth
                        error={voucherError ? true : false}
                        classes={{ input: classes.padless }}
                      />
                      <FormHelperText
                        error={
                          voucherError && voucherError !== "" ? true : false
                        }
                      >
                        {voucherError && voucherError !== ""
                          ? voucherError
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2} className={classes.deleteColumn}>
                    <Button
                      id="button-sendinboxstep3-addvoucher"
                      variant="contained"
                      color="primary"
                      type="submit"
                      classes={{ input: classes.padless }}
                      disableElevation
                      fullWidth
                    >
                      {t("106")}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          </Box>
          <form>
            <Box py={2}>
              <Grid
                container
                item
                xs={12}
                alignItems="center"
                sx={{
                  border: "1px solid rgba(0, 0, 0, 0.1  )",
                  padding: 2,
                  borderRadius: 2,
                  background: "#344f36",
                }}
              >
                <Grid item xs={12} sm={3} md={5}>
                  <Typography variant="subtitl2" color={"#FFF"}>
                    {t("364")}
                    {isMobile && (
                      <>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          color="primary"
                          onClick={(event) => {
                            setAnchorEl(event.currentTarget);
                          }}
                          size="large"
                        >
                          <HelpOutline />
                        </IconButton>
                        <Popover
                          id={popoverId}
                          open={openPopover}
                          anchorEl={anchorEl}
                          onClose={() => {
                            setAnchorEl(null);
                          }}
                          onClick={() => {
                            setAnchorEl(null);
                          }}
                          anchorReference="anchorPosition"
                          anchorPosition={{ top: 25, left: 400 }}
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                        >
                          <DonationSummary showClose />
                        </Popover>
                      </>
                    )}
                  </Typography>
                </Grid>
                <Grid
                  container
                  item
                  direction={"column"}
                  xs={12}
                  sm={9}
                  md={7}
                  justifyContent="space-between"
                >
                  <Grid
                    container
                    item
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-end"
                    spacing={2}
                  >
                    <DonationSelector
                      currency={currency}
                      selectedDonation={startpopOrder.donation}
                      submitDonation={submitDonation}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </form>
          <Box my={4}>
            <Grid container direction="column" py={1} alignItems="start">
              <Typography variant="h5" gutterBottom color={"primary"}>
                {t("517")}
              </Typography>
              <Typography variant="body1" sx={{ mr: 1 }}>
                {t("518")}
              </Typography>
            </Grid>

            <Grid container direction="row" py={1} alignItems="center">
              <AdditionalProducts
                onAddToCart={addProductToOrder}
              ></AdditionalProducts>
            </Grid>
          </Box>
          <Box my={4}>
            <Grid
              container
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Grid item>
                <Button
                  id="button-sendinboxstep3-back"
                  color="primary"
                  onClick={history.goBack}
                  variant="outlined"
                >
                  <ChevronLeft />
                </Button>
              </Grid>
              <Grid item>
                <Button
                  id="button-sendinboxstep3-submitwithpayment"
                  color="primary"
                  onClick={submitHandler}
                  variant="contained"
                  disableElevation
                >
                  <ChevronRight />
                </Button>
              </Grid>
            </Grid>
          </Box>
        </WBPPage>
      </StartpopOrderSideBar>
      <CornutaWarningModal
        warningTextObject={warningTexts}
        osmiaType={
          settings.osmiaTypeSeason === "cornuta"
            ? "Osmia cornuta"
            : "Osmia bicornis"
        }
        changeAcceptValue={() => {
          setAcceptedWarning(true);
        }}
        open={warningModalOpenStatus}
        onClose={() => {
          setWarningModalOpenStatus(false);
        }}
      />
    </div>
  );
};

export default StartpopOrderSummary;
